'use client';
import ChevronRight from '@/assets/svgs/chevron-right-small.svg';
import { inter600 } from '@/config/fonts';
import useToast from '@/containers/ToastMessage/hooks/useToast';
import {
  selectAccountDetails,
  selectAllAccounts,
  selectCurrentAccount,
  selectCurrentAccountId,
  selectRootAccounts,
} from '@/lib/redux/app.selectors';
import { Account } from '@/lib/redux/app.slice';
import useAccountSwitching from '@/shared/hooks/useAccountSwitching';
import { useRolePermissions } from '@/shared/hooks/useRolePermissions';
import { copyToClipboard } from '@/shared/utils/clipboard';
import { canSwitchToAccount, getParentChain } from '@/shared/utils/accountHierarchy';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import AccountAvatar from './AccountAvatar';
import AccountSelectorDropdown from './AccountSelectorDropdown';
import AccountSelectorLoader from './AccountSelectorLoader';
import { useAccountSelectorData } from './useAccountSelectorData';

interface AccountSelectorProps {
  className?: string;
}

const AccountSelector = ({ className }: AccountSelectorProps) => {
  // Redux selectors for UI state
  const currentAccountId = useSelector(selectCurrentAccountId);
  const currentAccount = useSelector(selectCurrentAccount);
  const accountDetails = useSelector(selectAccountDetails);
  const allAccounts = useSelector(selectAllAccounts);
  const rootAccounts = useSelector(selectRootAccounts);

  // Local UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Custom hooks for business logic
  const { switchToAccount } = useAccountSwitching();
  const { isLoading } = useAccountSelectorData();
  const { showSuccess } = useToast();
  const { isSuper } = useRolePermissions();

  // Calculate dropdown position relative to trigger element
  const calculateDropdownPosition = useCallback(() => {
    if (!triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const dropdownWidth = 320; // 80 * 4 (w-80 = 20rem = 320px)
    const dropdownHeight = 400; // maxHeight from style

    // Calculate preferred position (to the right)
    let left = triggerRect.right + 8;
    let top = triggerRect.top;

    // Check if dropdown would go off-screen to the right
    if (left + dropdownWidth > viewportWidth) {
      // Position to the left of trigger instead
      left = triggerRect.left - dropdownWidth - 8;
    }

    // Check if dropdown would go off-screen at the bottom
    if (top + dropdownHeight > viewportHeight) {
      // Align bottom of dropdown with bottom of viewport
      top = viewportHeight - dropdownHeight - 8;
    }

    // Ensure dropdown doesn't go above viewport
    if (top < 8) {
      top = 8;
    }

    setDropdownPosition({ top, left });
  }, []);

  // Handle dropdown toggle
  const handleDropdownToggle = () => {
    if (!isDropdownOpen) {
      calculateDropdownPosition();
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Handle keyboard events for accessibility
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleDropdownToggle();
    }
  };

  // TODO: Implement pre-fetching for frequently accessed accounts
  // TODO: Store recently accessed accounts in localStorage for pre-fetching
  // TODO: Add loading states for individual account fetching
  // TODO: Consider caching strategy for account data

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedOutsideDropdown =
        dropdownRef.current && !dropdownRef.current.contains(target);
      const clickedOutsideTrigger =
        triggerRef.current && !triggerRef.current.contains(target);

      if (clickedOutsideDropdown && clickedOutsideTrigger) {
        setIsDropdownOpen(false);
        setSearchTerm('');
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  // Recalculate position on window resize
  useEffect(() => {
    const handleResize = () => {
      if (isDropdownOpen) {
        calculateDropdownPosition();
      }
    };

    if (isDropdownOpen) {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [isDropdownOpen, calculateDropdownPosition]);

  // Current account display name
  const currentAccountName = currentAccount?.account_name || accountDetails?.account_name;
  const currentAccountDisplayId = currentAccount?.account_id || currentAccountId;

  // Get parent chain for current account
  const parentChain = useMemo(() => {
    return getParentChain(currentAccountId, allAccounts);
  }, [currentAccountId, allAccounts]);

  // Permission check function for account switching
  const canUserSwitchTo = useCallback(
    (targetAccountId: string) => {
      return canSwitchToAccount(targetAccountId, allAccounts, rootAccounts, isSuper);
    },
    [allAccounts, rootAccounts, isSuper]
  );

  // Filter accounts based on search term and sort with current account first
  // Also exclude accounts that are in the parent chain to avoid duplicates
  const filteredAccounts = useMemo(() => {
    if (!allAccounts) return [];

    // Get IDs of accounts in the parent chain
    const parentChainIds = new Set(parentChain.map((acc) => acc.account_id));

    // Filter accounts based on search term
    const filtered = !searchTerm
      ? allAccounts
      : allAccounts.filter(
          (account) =>
            account.account_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            account.account_id.toLowerCase().includes(searchTerm.toLowerCase())
        );

    // Exclude accounts that are in the parent chain (only when not searching)
    const withoutParentChain = !searchTerm
      ? filtered.filter((account) => !parentChainIds.has(account.account_id))
      : filtered;

    // Sort to put current account first (create copy since Redux state is immutable)
    return [...withoutParentChain].sort((a, b) => {
      if (a.account_id === currentAccountId) return -1;
      if (b.account_id === currentAccountId) return 1;
      return 0;
    });
  }, [allAccounts, searchTerm, currentAccountId, parentChain]);

  // Handle account selection
  const handleAccountSelect = useCallback(
    async (account: Account) => {
      // TODO: Add account switching loading states
      // TODO: Implement account switching animations
      // TODO: Add keyboard navigation for dropdown
      setIsDropdownOpen(false);
      setSearchTerm('');
      await switchToAccount(account.account_id);
    },
    [switchToAccount]
  );

  // Handle copying account name to clipboard
  const handleCopyAccountName = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (currentAccountName) {
        copyToClipboard(currentAccountName, showSuccess);
      }
    },
    [currentAccountName, showSuccess]
  );

  // Handle copying account ID to clipboard
  const handleCopyAccountId = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (currentAccountDisplayId) {
        copyToClipboard(currentAccountDisplayId, showSuccess);
      }
    },
    [currentAccountDisplayId, showSuccess]
  );

  // Show loader while data is loading or if we don't have the required display info
  if (isLoading || !currentAccountName || !currentAccountDisplayId) {
    return (
      <div className={classNames('account-chooser h-14', className)}>
        <AccountSelectorLoader />
      </div>
    );
  }

  return (
    <div className={classNames('account-chooser h-14', className)}>
      <div className="dropdown dropdown-right w-full">
        {/* Current Account Display */}
        <div className="my-2 flex w-full items-center justify-between rounded-lg p-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {/* Avatar */}
            <AccountAvatar
              accountId={currentAccountDisplayId}
              accountName={currentAccountName}
              size="md"
            />

            {/* Account Details */}
            <div className="min-w-0 flex-1">
              <div
                className={classNames(
                  inter600.className,
                  'text-md h-5 cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap'
                )}
                title={`${currentAccountName} (click to copy)`}
                onClick={handleCopyAccountName}>
                {currentAccountName}
              </div>
              <div
                className={classNames(
                  inter600.className,
                  'cursor-pointer overflow-hidden text-xs leading-5 text-ellipsis whitespace-nowrap text-gray-600'
                )}
                title={`${currentAccountDisplayId} (click to copy)`}
                onClick={handleCopyAccountId}>
                {currentAccountDisplayId}
              </div>
            </div>
          </div>

          {/* Dropdown Button */}
          <button
            ref={triggerRef}
            tabIndex={0}
            className="btn btn-square btn-ghost btn-xs flex-shrink-0 rounded-lg"
            onClick={handleDropdownToggle}
            onKeyDown={handleKeyDown}
            aria-label="Open account selector">
            <ChevronRight className="text-gray-600" />
          </button>
        </div>
      </div>

      {/* Dropdown Component */}
      <AccountSelectorDropdown
        isOpen={isDropdownOpen}
        position={dropdownPosition}
        dropdownRef={dropdownRef}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filteredAccounts={filteredAccounts}
        currentAccountId={currentAccountId}
        onAccountSelect={handleAccountSelect}
        parentChain={parentChain}
        canSwitchTo={canUserSwitchTo}
      />
    </div>
  );
};

export default AccountSelector;
